import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Gift, Plus, Eye, Users, Calendar, MapPin, Trash2, Edit, ExternalLink } from 'lucide-react';
import { formatDate } from '../lib/utils';

const TYPE_COLORS = {
  job_fair: 'bg-amber-50 text-amber-700',
  job_posting: 'bg-blue-50 text-blue-700',
  food_distribution: 'bg-emerald-50 text-emerald-700',
  clothing_drive: 'bg-purple-50 text-purple-700',
  housing: 'bg-sky-50 text-sky-700',
  training: 'bg-orange-50 text-orange-700',
  recovery_meeting: 'bg-teal-50 text-teal-700',
  transportation: 'bg-slate-50 text-slate-700',
  community_event: 'bg-rose-50 text-rose-700',
  scholarship: 'bg-indigo-50 text-indigo-700',
  volunteer: 'bg-pink-50 text-pink-700',
};

export default function MyOpportunitiesPage() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const sortParam = searchParams.get('sort');
  const [opportunities, setOpportunities] = useState([]);
  const [filter, setFilter] = useState(statusParam || 'all');
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const api = axiosAuth();

  const load = () => {
    api.get('/opportunities/mine').then(r => {
      setOpportunities(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (statusParam) {
      setFilter(statusParam);
    }
  }, [statusParam]);

  const filtered = filter === 'all' 
    ? opportunities 
    : opportunities.filter(o => o.status === filter);

  const sorted = [...filtered].sort((a, b) => {
    if (sortParam === 'views') return (b.views || 0) - (a.views || 0);
    if (sortParam === 'interests') return (b.interests || 0) - (a.interests || 0);
    return 0;
  });

  const handleDelete = async (id) => {
    try {
      await api.delete(`/opportunities/${id}`);
      toast.success('Opportunity removed');
      load();
      setDeleteId(null);
    } catch (e) {
      toast.error('Failed to delete opportunity');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-opportunities-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>My Opportunities</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage your posted opportunities</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32 rounded-full" data-testid="my-opportunities-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="deleted">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => navigate('/post-opportunity')} 
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2"
            data-testid="new-opp-button"
          >
            <Plus size={16} /> New
          </Button>
        </div>
      </div>

      {sortParam === 'views' ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-700" data-testid="my-opportunities-sort">
          Sorted by total views
        </div>
      ) : sortParam === 'interests' ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-700" data-testid="my-opportunities-sort">
          Sorted by interests received
        </div>
      ) : null}

      {sorted.length > 0 ? (
        <div className="space-y-3">
          {sorted.map(opp => (
            <Card key={opp.id} className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${TYPE_COLORS[opp.opportunity_type]?.split(' ')[0] || 'bg-slate-50'}`}>
                      <Gift className={`w-5 h-5 ${TYPE_COLORS[opp.opportunity_type]?.split(' ')[1] || 'text-slate-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold">{opp.title}</p>
                          <Badge className={TYPE_COLORS[opp.opportunity_type] || 'bg-slate-100 text-slate-600'}>
                            {opp.opportunity_type.replace('_', ' ')}
                          </Badge>
                        </div>
                        <Badge className={opp.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                          {opp.status}
                        </Badge>
                      </div>
                      {opp.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{opp.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye size={12} /> {opp.views || 0} views
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={12} /> {opp.interests || 0} interested
                        </span>
                        {opp.date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={12} /> {opp.date}
                          </span>
                        )}
                        {opp.location && (
                          <span className="flex items-center gap-1">
                            <MapPin size={12} /> {opp.location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex sm:flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate(`/opportunities/${opp.id}`)}
                      className="rounded-full text-xs flex-1 sm:flex-none"
                    >
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                    {opp.status === 'active' && (
                      <Dialog open={deleteId === opp.id} onOpenChange={(open) => setDeleteId(open ? opp.id : null)}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="rounded-full text-xs text-red-600 hover:bg-red-50 flex-1 sm:flex-none">
                            <Trash2 size={14} className="mr-1" /> Remove
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-2xl">
                          <DialogHeader>
                            <DialogTitle>Remove Opportunity?</DialogTitle>
                          </DialogHeader>
                          <p className="text-sm text-muted-foreground">
                            This will archive the opportunity and hide it from the feed. You can restore it later.
                          </p>
                          <div className="flex gap-2 mt-4">
                            <Button variant="outline" onClick={() => setDeleteId(null)} className="flex-1 rounded-full">
                              Cancel
                            </Button>
                            <Button variant="destructive" onClick={() => handleDelete(opp.id)} className="flex-1 rounded-full">
                              Remove
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-2 border-dashed border-emerald-200 bg-emerald-50/30 rounded-2xl">
          <CardContent className="py-12 text-center">
            <Gift className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
            <p className="text-muted-foreground mb-4">
              {filter === 'all' ? "You haven't posted any opportunities yet." : `No ${filter} opportunities.`}
            </p>
            <Button 
              onClick={() => navigate('/post-opportunity')} 
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
            >
              <Plus size={16} className="mr-2" /> Post Your First Opportunity
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
